'use client';
import { trackedEntity } from '@cineamo/pulse';
import { usePulseTracker } from '@cineamo/pulse/react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useState } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { Client } from '@cineamo/api/constants/Client.enum';
import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { appendReservationShowingId } from '@/helpers/reservation-ticket-url-helper';
import { onTicketButtonClick } from '@/helpers/ticket-purchase-intent-helper';
import { isDatetimePast } from '@/helpers/time-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { getOnlineTicketUrl } from '@cineamo/ui/src/helpers/showing-helper';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

import { NoOnlineTicketUrlModal } from '../NoOnlineTicketUrlModal';
import TicketShape from '../ticket-shape/TicketShape';

const BuyTicketButton: React.FC<{
    onBuy: (ticketUrl: string) => void;
    showing: ShowingDto;
    movieId?: number;
    className?: string;
    /**
     * Renders the CTA as a real, crawlable `<a href>` instead of a client-only button, and gates on
     * past showings with a disabled state — used on the canonical showing detail page so the ticket
     * link is followable without JS (SEO). The `ShowingInfoModal` usage keeps the original button,
     * whose `onBuy` callback (not a URL) drives navigation there, so it's left untouched by default.
     */
    renderAsLink?: boolean;
}> = ({ onBuy, showing, movieId, className, renderAsLink }) => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const { track: pulseTrack } = usePulseTracker();

    const [noTicketUrlIsOpen, setNoTicketUrlIsOpen] = useState(false);
    const ticketUrl = appendReservationShowingId(
        getOnlineTicketUrl(showing, Client.CinemaWebsite) ?? undefined,
        showing.id,
        domainConfiguration.domain
    );
    const hasTicketUrl = (ticketUrl?.length ?? 0) > 0;
    const isExternal = hasTicketUrl && !ticketUrl?.startsWith('/');
    const isPast = renderAsLink && isDatetimePast(showing.startDatetime, true);

    function trackTicketClick() {
        onTicketButtonClick(showing.id, PurchaseReasonEnum.CINEMA_WEBSITE, showing.contentId);
        pulseTrack({
            ...trackedEntity({ movieId, contentId: showing.contentId }),
            event_type: 'ticket_purchase_intent',
            context: { showing_id: showing.id }
        });
    }

    const ticketShape = (
        <TicketShape
            style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0' }}
            config={{
                fillColorHex: domainConfiguration.style.primaryColor,
                fillColorOpacity: isPast ? 0.4 : 1
            }}>
            <span
                className={cn(
                    'leading-[24px] px-12 py-6 relative text-body-bold-16 block',
                    isPast && 'opacity-60',
                    'text-[var(--on-primary-color)]'
                )}>
                {t('showing-info.cta')}
            </span>
        </TicketShape>
    );

    return (
        <>
            {noTicketUrlIsOpen && (
                <NoOnlineTicketUrlModal
                    modalIsOpen={noTicketUrlIsOpen}
                    setModalIsOpen={setNoTicketUrlIsOpen}
                />
            )}

            {isPast ? (
                <div
                    className={cn('w-full mt-16 cursor-not-allowed', className)}
                    aria-disabled="true">
                    {ticketShape}
                </div>
            ) : renderAsLink && hasTicketUrl ? (
                <a
                    className={cn('w-full mt-16 block', className)}
                    href={ticketUrl}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'nofollow noopener' : undefined}
                    onClick={() => {
                        trackTicketClick();
                        if (ticketUrl) onBuy(ticketUrl);
                    }}>
                    {ticketShape}
                </a>
            ) : (
                <button
                    className={cn('w-full mt-16', className)}
                    onClick={() => {
                        if (!hasTicketUrl) {
                            setNoTicketUrlIsOpen(true);
                        } else {
                            trackTicketClick();
                            if (ticketUrl) onBuy(ticketUrl);
                        }
                    }}>
                    {ticketShape}
                </button>
            )}
        </>
    );
};

export default BuyTicketButton;
